import { BusinessObjectBase } from './base';
import { processFunctionParameterSchema } from '@schemas/business-objects/process-function-parameter';
import type { ProcessDataType } from './process-data-type';
import type { ProcessFunction } from './process-function';
import type { Id } from '../base-types';

export interface ProcessFunctionParameterData {
  readonly id: Id;
  readonly name?: string;
  readonly isArray?: boolean;
  readonly sequence?: number;
  readonly description?: string;
  readonly functionId: Id;
  readonly function?: ProcessFunction;
  readonly typeId: Id;
  readonly type?: ProcessDataType;
}

export interface ProcessFunctionParameter extends Readonly<ProcessFunctionParameterData> {}
type ProcessFunctionParameterCreateWithChildrenData =
  Partial<ProcessFunctionParameterData>;

export class ProcessFunctionParameter extends BusinessObjectBase {
  static readonly entityName = 'ProcessFunctionParameter' as const;
  static readonly plural = 'process-function-parameters';
  static schema = processFunctionParameterSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<ProcessFunctionParameter[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new ProcessFunctionParameter(data));
  }

  static async findOne(filter?: any): Promise<ProcessFunctionParameter | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new ProcessFunctionParameter(data) : null;
  }

  static async findById(
    id: Id,
    filter?: any,
  ): Promise<ProcessFunctionParameter> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new ProcessFunctionParameter(data);
  }

  static async create(
    data: Omit<ProcessFunctionParameterData, 'id'>,
  ): Promise<ProcessFunctionParameter> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new ProcessFunctionParameter(result);
  }

  static async createMany(
    data: Omit<ProcessFunctionParameterData, 'id'>[],
  ): Promise<ProcessFunctionParameter[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new ProcessFunctionParameter(item));
  }

  static async createWithChildren(
    data: ProcessFunctionParameterCreateWithChildrenData,
  ): Promise<ProcessFunctionParameter> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new ProcessFunctionParameter(result);
  }

  static async createManyWithChildren(
    data: ProcessFunctionParameterCreateWithChildrenData[],
  ): Promise<ProcessFunctionParameter[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new ProcessFunctionParameter(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<ProcessFunctionParameterData>,
  ): Promise<ProcessFunctionParameter> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new ProcessFunctionParameter(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
